/**
 * MyPayrollPage — Personal payroll self-service dashboard.
 *
 * Thin wrapper that reuses existing subgraph-payroll hooks filtered
 * to the current user. Backend-driven per Dumb Frontend (DRR-055).
 *
 * Legislation backing:
 * - FLSA 29 USC 207: Overtime calculation, pay period transparency
 * - MN 181.032: Earnings statement must show hours, rate, deductions, net pay
 * - SOX 302/404: Payroll audit trail, dual approval before disbursement
 *
 * Data sources:
 * - payrollPayStubs: Pay stub history
 * - payrollWithholdings: W-4 and state withholding elections
 * - payrollTaxSummarys: YTD tax withholdings by jurisdiction
 * - payrollDeductions: Benefit deductions (401k, health, etc.)
 * - payrollTimeEntries: Today's time entries (clock in/out)
 *
 * Route: /ferp-core/my-payroll
 * Scope: payroll:self:read (from role-0-0-1 base employee role)
 */

import React from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCash,
  IconClock,
  IconClockPlay,
  IconClockStop,
  IconFileInvoice,
  IconHistory,
  IconMinus,
  IconReceipt2,
  IconBuildingBank,
  IconAlertCircle,
  IconSend,
} from '@tabler/icons-react';
import { useMyActor } from '@/hooks/useMyActor';
import { useAuth } from '@/auth/AuthContext';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useGpsPunchIn, useGpsPunchOut, useCurrentLocation } from '@ferp/workforce';

// GraphQL queries for employee self-service data
const GET_MY_PAY_STUBS = gql`
  query PayrollPayStubs($tenantNum: Int!, $limit: Int) {
    payrollPayStubs(tenantNum: $tenantNum, limit: $limit) {
      id
      periodEnd
      periodStart
      payDate
      grossPay { cents currency }
      netPay { cents currency }
      regularHours
      overtimeHours
      federalTax { cents currency }
      stateTax { cents currency }
      ytdGross { cents currency }
      ytdNet { cents currency }
    }
  }
`;

const GET_MY_WITHHOLDINGS = gql`
  query PayrollWithholdings($tenantNum: Int!) {
    payrollWithholdings(tenantNum: $tenantNum) {
      id
      jurisdiction
      jurisdictionType
      filingStatus
      allowances
      additionalWithholding { cents currency }
      isExempt
      formVersion
      effectiveDate
    }
  }
`;

const GET_MY_TAX_SUMMARY = gql`
  query PayrollTaxSummarys($tenantNum: Int!, $taxYear: Int) {
    payrollTaxSummarys(tenantNum: $tenantNum, taxYear: $taxYear) {
      id
      taxYear
      jurisdiction
      jurisdictionType
      taxableWages { cents currency }
      taxWithheld { cents currency }
      currentPeriodTax { cents currency }
      effectiveRate
    }
  }
`;

const GET_MY_DEDUCTIONS = gql`
  query PayrollDeductions($tenantNum: Int!) {
    payrollDeductions(tenantNum: $tenantNum) {
      code
      name
      deductionType
      amount
      isPercentage
      isPreTax
      ytdAmount { cents currency }
    }
  }
`;

const GET_MY_TIME_ENTRIES = gql`
  query PayrollTimeEntries($employeeId: String, $startDate: String, $endDate: String, $limit: Int) {
    payrollTimeEntries(employeeId: $employeeId, startDate: $startDate, endDate: $endDate, limit: $limit) {
      id tenantId employeeId entryType workDate startTime endTime
      hours rateType status notes createdAt updatedAt
    }
  }
`;

const SUBMIT_TIMESHEET = gql`
  mutation SubmitPayrollTimesheet($input: PayrollSubmitTimesheetInput!) {
    submitPayrollTimesheet(input: $input) {
      id status submittedAt
    }
  }
`;

const GET_OR_CREATE_MY_TIMESHEET = gql`
  mutation GetOrCreateMyTimesheet {
    getOrCreateMyTimesheet {
      id
      tenantId
      employeeId
      payPeriodStart
      payPeriodEnd
      status
      totalRegularHours
      totalOvertimeHours
      totalDoubleTimeHours
      totalBreakHours
      totalPtoHours
      submittedBy
      submittedAt
      approvedBy
      approvedAt
      rejectionReason
    }
  }
`;

// Format cents to dollars
function formatMoney(money: { cents: number; currency: string } | null | undefined): string {
  if (!money) return '$0.00';
  const dollars = money.cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: money.currency || 'USD',
  }).format(dollars);
}

// Format date
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function MyPayrollPage() {
  const { actorId, loading: actorLoading } = useMyActor();
  const { tenant } = useAuth();
  const tenantNum = tenant?.num ?? 0;

  // Fetch pay stubs
  const { data: payStubsData, loading: payStubsLoading } = useQuery(GET_MY_PAY_STUBS, {
    variables: { tenantNum, limit: 5 },
    skip: !tenantNum,
  });

  // Fetch withholdings
  const { data: withholdingsData, loading: withholdingsLoading } = useQuery(GET_MY_WITHHOLDINGS, {
    variables: { tenantNum },
    skip: !tenantNum,
  });

  // Fetch tax summary
  const { data: taxSummaryData, loading: taxSummaryLoading } = useQuery(GET_MY_TAX_SUMMARY, {
    variables: { tenantNum, taxYear: new Date().getFullYear() },
    skip: !tenantNum,
  });

  // Fetch deductions
  const { data: deductionsData, loading: deductionsLoading } = useQuery(GET_MY_DEDUCTIONS, {
    variables: { tenantNum },
    skip: !tenantNum,
  });

  // Fetch time entries for today
  const today = new Date().toISOString().split('T')[0];
  const { data: timeEntriesData, loading: timeEntriesLoading, refetch: refetchTimeEntries } = useQuery(GET_MY_TIME_ENTRIES, {
    variables: { employeeId: actorId, startDate: today, endDate: today, limit: 50 },
    skip: !actorId,
  });

  // GPS punch hooks
  const clockIn = useGpsPunchIn();
  const clockOut = useGpsPunchOut();
  const { getCurrentPosition } = useCurrentLocation();
  const [submitTimesheet] = useMutation(SUBMIT_TIMESHEET);
  const [getOrCreateTimesheet, { data: timesheetData, loading: timesheetLoading }] = useMutation(GET_OR_CREATE_MY_TIMESHEET);
  const [isPunching, setIsPunching] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Fetch current timesheet on mount
  React.useEffect(() => {
    if (actorId) {
      getOrCreateTimesheet();
    }
  }, [actorId, getOrCreateTimesheet]);

  const payStubs = payStubsData?.payrollPayStubs ?? [];
  const withholdings = withholdingsData?.payrollWithholdings ?? [];
  const taxSummaries = taxSummaryData?.payrollTaxSummarys ?? [];
  const deductions = deductionsData?.payrollDeductions ?? [];
  const timeEntries = timeEntriesData?.payrollTimeEntries ?? [];
  const currentTimesheet = timesheetData?.getOrCreateMyTimesheet;

  // Find open time entry (clocked in but not out)
  const openEntry = timeEntries.find((e: Record<string, unknown>) => e.status === 'clocked_in');
  const todayHours = timeEntries.reduce((sum: number, e: Record<string, unknown>) => sum + ((e.hours as number) ?? 0), 0);

  const latestStub = payStubs[0];
  const isLoading = actorLoading || payStubsLoading || withholdingsLoading || taxSummaryLoading || deductionsLoading || timeEntriesLoading || timesheetLoading;

  // Timesheet status helpers
  const canSubmitTimesheet = currentTimesheet?.status === 'draft' && !openEntry;
  const timesheetSubmitted = currentTimesheet?.status === 'submitted';
  const timesheetApproved = currentTimesheet?.status === 'approved';

  // Handle clock in
  const handleClockIn = async () => {
    setIsPunching(true);
    try {
      const position = await getCurrentPosition();
      clockIn.mutate({
        employeeId: actorId,
        source: 'WEB',
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      }, {
        onSuccess: () => refetchTimeEntries(),
        onError: (err: Error) => console.error('Clock in failed:', err),
      });
    } catch (err) {
      console.error('GPS unavailable, clocking in without location');
      clockIn.mutate({ employeeId: actorId, source: 'WEB' }, {
        onSuccess: () => refetchTimeEntries(),
      });
    } finally {
      setIsPunching(false);
    }
  };

  // Handle clock out
  const handleClockOut = async () => {
    if (!openEntry) return;
    setIsPunching(true);
    try {
      const position = await getCurrentPosition();
      clockOut.mutate({
        timeEntryId: openEntry.id,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      }, {
        onSuccess: () => refetchTimeEntries(),
        onError: (err: Error) => console.error('Clock out failed:', err),
      });
    } catch (err) {
      console.error('GPS unavailable, clocking out without location');
      clockOut.mutate({ timeEntryId: openEntry.id as string }, {
        onSuccess: () => refetchTimeEntries(),
      });
    } finally {
      setIsPunching(false);
    }
  };

  // Handle submit timesheet
  const handleSubmitTimesheet = async () => {
    if (!currentTimesheet?.id) return;
    setIsSubmitting(true);
    try {
      await submitTimesheet({
        variables: { input: { timesheetId: currentTimesheet.id } },
      });
      // Refresh timesheet data
      await getOrCreateTimesheet();
    } catch (err) {
      console.error('Failed to submit timesheet:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Center h="60vh">
        <Loader size="lg" />
      </Center>
    );
  }

  // Calculate totals
  const ytdGross = latestStub?.ytdGross?.cents ?? 0;
  const ytdNet = latestStub?.ytdNet?.cents ?? 0;
  const totalTaxWithheld = taxSummaries.reduce(
    (sum: number, t: { taxWithheld?: { cents: number } }) => sum + (t.taxWithheld?.cents ?? 0),
    0
  );

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group gap="xs">
          <ThemeIcon size="lg" variant="light" color="green">
            <IconCash size={20} />
          </ThemeIcon>
          <Title order={2}>My Payroll</Title>
        </Group>

        {/* Summary cards */}
        <SimpleGrid cols={{ base: 1, sm: 4 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">YTD Gross</Text>
              <ThemeIcon size="sm" variant="light" color="green">
                <IconCash size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{formatMoney({ cents: ytdGross, currency: 'USD' })}</Text>
            <Text size="xs" c="dimmed">Year to date</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">YTD Net</Text>
              <ThemeIcon size="sm" variant="light" color="blue">
                <IconFileInvoice size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{formatMoney({ cents: ytdNet, currency: 'USD' })}</Text>
            <Text size="xs" c="dimmed">Take-home pay</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">YTD Taxes</Text>
              <ThemeIcon size="sm" variant="light" color="orange">
                <IconBuildingBank size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{formatMoney({ cents: totalTaxWithheld, currency: 'USD' })}</Text>
            <Text size="xs" c="dimmed">All jurisdictions</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Actor ID</Text>
              <ThemeIcon size="sm" variant="light" color="gray">
                <IconReceipt2 size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{actorId ?? '—'}</Text>
          </Card>
        </SimpleGrid>

        {/* Tab content */}
        <Tabs defaultValue="time">
          <Tabs.List>
            <Tabs.Tab value="time" leftSection={<IconClock size={14} />}>
              Time {openEntry ? '(Clocked In)' : ''}
            </Tabs.Tab>
            <Tabs.Tab value="stubs" leftSection={<IconFileInvoice size={14} />}>
              Pay Stubs ({payStubs.length})
            </Tabs.Tab>
            <Tabs.Tab value="withholdings" leftSection={<IconReceipt2 size={14} />}>
              Withholdings ({withholdings.length})
            </Tabs.Tab>
            <Tabs.Tab value="taxes" leftSection={<IconBuildingBank size={14} />}>
              Tax Summary ({taxSummaries.length})
            </Tabs.Tab>
            <Tabs.Tab value="deductions" leftSection={<IconMinus size={14} />}>
              Deductions ({deductions.length})
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="time" pt="md">
            <Stack gap="md">
              {/* Clock In/Out Card */}
              <Card withBorder padding="lg">
                <Group justify="space-between" mb="md">
                  <Group gap="xs">
                    <ThemeIcon size="lg" variant="light" color={openEntry ? 'green' : 'gray'}>
                      <IconClock size={20} />
                    </ThemeIcon>
                    <Stack gap={0}>
                      <Text fw={600}>{openEntry ? 'Currently Clocked In' : 'Not Clocked In'}</Text>
                      <Text size="xs" c="dimmed">
                        {openEntry
                          ? `Since ${new Date(openEntry.startTime as string).toLocaleTimeString()}`
                          : 'Click Clock In to start your shift'}
                      </Text>
                    </Stack>
                  </Group>
                  <Group>
                    {!openEntry ? (
                      <Button
                        leftSection={<IconClockPlay size={16} />}
                        color="green"
                        onClick={handleClockIn}
                        loading={isPunching || clockIn.isPending}
                      >
                        Clock In
                      </Button>
                    ) : (
                      <Button
                        leftSection={<IconClockStop size={16} />}
                        color="red"
                        onClick={handleClockOut}
                        loading={isPunching || clockOut.isPending}
                      >
                        Clock Out
                      </Button>
                    )}
                  </Group>
                </Group>
                <Group gap="xl">
                  <Stack gap={0}>
                    <Text size="sm" c="dimmed">Today's Hours</Text>
                    <Text size="xl" fw={700}>{todayHours.toFixed(2)}</Text>
                  </Stack>
                  <Stack gap={0}>
                    <Text size="sm" c="dimmed">Entries Today</Text>
                    <Text size="xl" fw={700}>{timeEntries.length}</Text>
                  </Stack>
                </Group>
              </Card>

              {/* Today's Time Entries */}
              {timeEntries.length === 0 ? (
                <Alert color="blue" title="No Time Entries Today" icon={<IconClock size={16} />}>
                  No time entries recorded today. Clock in to start tracking your time.
                </Alert>
              ) : (
                <Paper withBorder>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Type</Table.Th>
                        <Table.Th>Start</Table.Th>
                        <Table.Th>End</Table.Th>
                        <Table.Th ta="right">Hours</Table.Th>
                        <Table.Th>Status</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {timeEntries.map((entry: Record<string, unknown>) => (
                        <Table.Tr key={entry.id as string}>
                          <Table.Td>
                            <Badge size="sm" color={entry.entryType === 'Overtime' ? 'orange' : 'blue'}>
                              {entry.entryType as string}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            {entry.startTime
                              ? new Date(entry.startTime as string).toLocaleTimeString()
                              : '—'}
                          </Table.Td>
                          <Table.Td>
                            {entry.endTime
                              ? new Date(entry.endTime as string).toLocaleTimeString()
                              : '—'}
                          </Table.Td>
                          <Table.Td ta="right" fw={500}>
                            {((entry.hours as number) ?? 0).toFixed(2)}
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              color={
                                entry.status === 'clocked_in' ? 'green' :
                                entry.status === 'clocked_out' ? 'blue' :
                                entry.status === 'approved' ? 'teal' : 'gray'
                              }
                            >
                              {entry.status as string}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              )}

              {/* Current Timesheet Summary */}
              {currentTimesheet && (
                <Card withBorder padding="lg">
                  <Group justify="space-between" mb="md">
                    <Group gap="xs">
                      <ThemeIcon size="lg" variant="light" color={
                        timesheetApproved ? 'green' :
                        timesheetSubmitted ? 'blue' : 'gray'
                      }>
                        <IconSend size={20} />
                      </ThemeIcon>
                      <Stack gap={0}>
                        <Text fw={600}>
                          Timesheet: {currentTimesheet.payPeriodStart} to {currentTimesheet.payPeriodEnd}
                        </Text>
                        <Text size="xs" c="dimmed">
                          Status: {currentTimesheet.status.charAt(0).toUpperCase() + currentTimesheet.status.slice(1)}
                          {currentTimesheet.submittedAt && ` • Submitted ${formatDate(currentTimesheet.submittedAt)}`}
                          {currentTimesheet.approvedAt && ` • Approved ${formatDate(currentTimesheet.approvedAt)}`}
                        </Text>
                      </Stack>
                    </Group>
                    {canSubmitTimesheet && (
                      <Button
                        leftSection={<IconSend size={16} />}
                        color="blue"
                        onClick={handleSubmitTimesheet}
                        loading={isSubmitting}
                      >
                        Submit Timesheet
                      </Button>
                    )}
                    {timesheetSubmitted && (
                      <Badge size="lg" color="blue" variant="light">
                        Pending Approval
                      </Badge>
                    )}
                    {timesheetApproved && (
                      <Badge size="lg" color="green" variant="light">
                        Approved
                      </Badge>
                    )}
                  </Group>
                  <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
                    <Stack gap={0}>
                      <Text size="sm" c="dimmed">Regular Hours</Text>
                      <Text size="xl" fw={700}>{currentTimesheet.totalRegularHours.toFixed(2)}</Text>
                    </Stack>
                    <Stack gap={0}>
                      <Text size="sm" c="dimmed">Overtime Hours</Text>
                      <Text size="xl" fw={700} c={currentTimesheet.totalOvertimeHours > 0 ? 'orange' : undefined}>
                        {currentTimesheet.totalOvertimeHours.toFixed(2)}
                      </Text>
                    </Stack>
                    <Stack gap={0}>
                      <Text size="sm" c="dimmed">Break Hours</Text>
                      <Text size="xl" fw={700}>{currentTimesheet.totalBreakHours.toFixed(2)}</Text>
                    </Stack>
                    <Stack gap={0}>
                      <Text size="sm" c="dimmed">Total Hours</Text>
                      <Text size="xl" fw={700} c="blue">
                        {(currentTimesheet.totalRegularHours + currentTimesheet.totalOvertimeHours).toFixed(2)}
                      </Text>
                    </Stack>
                  </SimpleGrid>
                  {openEntry && (
                    <Alert color="yellow" title="Clock Out Required" icon={<IconAlertCircle size={16} />} mt="md">
                      You must clock out before submitting your timesheet.
                    </Alert>
                  )}
                  {currentTimesheet.rejectionReason && (
                    <Alert color="red" title="Timesheet Rejected" icon={<IconAlertCircle size={16} />} mt="md">
                      {currentTimesheet.rejectionReason}
                    </Alert>
                  )}
                </Card>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="stubs" pt="md">
            {payStubs.length === 0 ? (
              <Alert color="blue" title="No Pay Stubs" icon={<IconFileInvoice size={16} />}>
                No pay stubs found. Your pay stubs will appear here after your first payroll run.
              </Alert>
            ) : (
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Pay Date</Table.Th>
                      <Table.Th>Period</Table.Th>
                      <Table.Th>Hours</Table.Th>
                      <Table.Th ta="right">Gross</Table.Th>
                      <Table.Th ta="right">Taxes</Table.Th>
                      <Table.Th ta="right">Net</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {payStubs.map((stub: Record<string, unknown>) => (
                      <Table.Tr key={stub.id as string}>
                        <Table.Td>{formatDate(stub.payDate as string)}</Table.Td>
                        <Table.Td>
                          {formatDate(stub.periodStart as string)} - {formatDate(stub.periodEnd as string)}
                        </Table.Td>
                        <Table.Td>
                          {(stub.regularHours as number) ?? 0}
                          {(stub.overtimeHours as number) > 0 && (
                            <Badge size="xs" color="orange" ml="xs">+{stub.overtimeHours as number} OT</Badge>
                          )}
                        </Table.Td>
                        <Table.Td ta="right">{formatMoney(stub.grossPay as { cents: number; currency: string })}</Table.Td>
                        <Table.Td ta="right">
                          {formatMoney({
                            cents:
                              ((stub.federalTax as { cents: number })?.cents ?? 0) +
                              ((stub.stateTax as { cents: number })?.cents ?? 0),
                            currency: 'USD',
                          })}
                        </Table.Td>
                        <Table.Td ta="right" fw={600}>{formatMoney(stub.netPay as { cents: number; currency: string })}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="withholdings" pt="md">
            {withholdings.length === 0 ? (
              <Alert color="blue" title="No Withholdings" icon={<IconReceipt2 size={16} />}>
                No withholding elections found. Update your W-4 to set up withholdings.
              </Alert>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {withholdings.map((w: Record<string, unknown>) => (
                  <Card key={w.id as string} withBorder padding="lg">
                    <Group justify="space-between" mb="md">
                      <Group gap="xs">
                        <Badge color={w.jurisdictionType === 'FEDERAL' ? 'blue' : 'teal'}>
                          {w.jurisdictionType as string}
                        </Badge>
                        <Text fw={600}>{w.jurisdiction as string}</Text>
                      </Group>
                      {(w.isExempt as boolean) && <Badge color="red">Exempt</Badge>}
                    </Group>
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Filing Status</Text>
                        <Text size="sm">{w.filingStatus as string}</Text>
                      </Group>
                      {w.allowances != null && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">Allowances</Text>
                          <Text size="sm">{w.allowances as number}</Text>
                        </Group>
                      )}
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Additional Withholding</Text>
                        <Text size="sm">{formatMoney(w.additionalWithholding as { cents: number; currency: string })}</Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Form Version</Text>
                        <Badge size="xs" variant="outline">{w.formVersion as string}</Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Effective Date</Text>
                        <Text size="sm">{formatDate(w.effectiveDate as string)}</Text>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="taxes" pt="md">
            {taxSummaries.length === 0 ? (
              <Alert color="blue" title="No Tax Data" icon={<IconBuildingBank size={16} />}>
                No tax withholding data found for this year.
              </Alert>
            ) : (
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Jurisdiction</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th ta="right">Taxable Wages</Table.Th>
                      <Table.Th ta="right">YTD Withheld</Table.Th>
                      <Table.Th ta="right">Current Period</Table.Th>
                      <Table.Th ta="right">Effective Rate</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {taxSummaries.map((t: Record<string, unknown>) => (
                      <Table.Tr key={t.id as string}>
                        <Table.Td fw={500}>{t.jurisdiction as string}</Table.Td>
                        <Table.Td>
                          <Badge size="sm" color={
                            t.jurisdictionType === 'FEDERAL' ? 'blue' :
                            t.jurisdictionType === 'STATE' ? 'teal' :
                            t.jurisdictionType === 'FICA' ? 'orange' : 'gray'
                          }>
                            {t.jurisdictionType as string}
                          </Badge>
                        </Table.Td>
                        <Table.Td ta="right">{formatMoney(t.taxableWages as { cents: number; currency: string })}</Table.Td>
                        <Table.Td ta="right" fw={600}>{formatMoney(t.taxWithheld as { cents: number; currency: string })}</Table.Td>
                        <Table.Td ta="right">{formatMoney(t.currentPeriodTax as { cents: number; currency: string })}</Table.Td>
                        <Table.Td ta="right">{(t.effectiveRate as number)?.toFixed(2)}%</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="deductions" pt="md">
            {deductions.length === 0 ? (
              <Alert color="blue" title="No Deductions" icon={<IconMinus size={16} />}>
                No benefit deductions found. Contact HR to set up your benefits.
              </Alert>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                {deductions.map((d: Record<string, unknown>) => (
                  <Card key={d.code as string} withBorder padding="lg">
                    <Group justify="space-between" mb="md">
                      <Text fw={600}>{d.name as string}</Text>
                      <Badge color={d.isPreTax ? 'green' : 'gray'} size="sm">
                        {d.isPreTax ? 'Pre-Tax' : 'Post-Tax'}
                      </Badge>
                    </Group>
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Amount</Text>
                        <Text size="sm">
                          {d.isPercentage ? `${d.amount as number}%` : formatMoney({ cents: (d.amount as number) * 100, currency: 'USD' })}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Type</Text>
                        <Badge size="xs" variant="outline">{d.deductionType as string}</Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">YTD Contributed</Text>
                        <Text size="sm" fw={500}>{formatMoney(d.ytdAmount as { cents: number; currency: string })}</Text>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
